import { httpBase } from "./backend";
/**
 * The market calendar, fetched once at load.
 *
 * Closures, whether we actually know them, and the day a date control should
 * open on. Beside the DSL and indicator catalogs, and inert the same way when no
 * URL is configured: the app has to run offline, and a date box that opens on
 * today rather than on the last trading day is a smaller failure than a blank
 * screen.
 *
 * **`known` is the field that matters.** An empty `holidays` means "no closures"
 * or "we never found out", and a client that cannot tell those apart will
 * happily let someone pick Christmas and then wonder why the chart did not move.
 *
 * `lastTradingDay` is computed by the server rather than here. Doing it in the
 * client would need the same walk-backwards rule and the same calendar, and two
 * implementations of a market calendar is one too many.
 */

const URL_BASE =
  httpBase(
    import.meta.env.VITE_QV_IND_URL as string | undefined,
    import.meta.env.VITE_QV_SET_URL as string | undefined,
  ) ||
  "";

export interface MarketCalendar {
  /** Closure dates as `yyyy-mm-dd`, in no particular order. */
  holidays: readonly string[];
  /** False when the calendar could not be read — not the same as "no closures". */
  known: boolean;
  /** The server's today, `yyyy-mm-dd`. */
  today: string;
  /** Today, or the most recent day the market was open before it. */
  lastTradingDay: string;
}

/** A usable answer with nothing in it, for when there is no server to ask. */
export function emptyCalendar(): MarketCalendar {
  const today = new Date();
  const iso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  return { holidays: [], known: false, today: iso, lastTradingDay: iso };
}

/**
 * The calendar, or an empty one. Never throws and never rejects: every caller
 * would turn a failure into the same fallback, so it is done once here.
 */
export async function fetchMarketCalendar(): Promise<MarketCalendar> {
  if (!URL_BASE) return emptyCalendar();
  try {
    const res = await fetch(`${URL_BASE.replace(/\/$/, "")}/sym/calendar`);
    if (!res.ok) return emptyCalendar();
    const body = (await res.json()) as Partial<MarketCalendar>;
    const fallback = emptyCalendar();
    return {
      holidays: Array.isArray(body.holidays) ? body.holidays : [],
      known: body.known === true,
      today: body.today || fallback.today,
      lastTradingDay: body.lastTradingDay || body.today || fallback.lastTradingDay,
    };
  } catch {
    return emptyCalendar();
  }
}
